import _ from "underscore";

import {
  PLUGIN_ADMIN_PERMISSIONS_DATABASE_ACTIONS,
  PLUGIN_ADMIN_PERMISSIONS_DATABASE_POST_ACTIONS,
  PLUGIN_ADVANCED_PERMISSIONS,
  PLUGIN_FEATURE_LEVEL_PERMISSIONS,
} from "metabase/plugins";
import {
  DataPermission,
  DataPermissionType,
  type DataPermissionValue,
  type DatabaseEntityId,
  type Group,
  type GroupsPermissions,
  type PermissionsDatabase,
  type SpecialGroupType,
} from "metabase-types/api";

import { DATA_PERMISSION_OPTIONS } from "../../constants/data-permissions";
import { Messages } from "../../constants/messages";
import {
  limitDatabasePermission,
  navigateToGranularPermissions,
} from "../../permissions";
import type { PermissionSectionConfig } from "../../types";
import { getSchemasPermission } from "../../utils/graph";
import {
  getPermissionWarning,
  getPermissionWarningModal,
  getViewDataPermissionsTooRestrictiveWarningModal,
} from "../confirmations";

import { getNativePermissionDisabledTooltip } from "./shared";

const buildAccessPermission = (
  entityId: DatabaseEntityId,
  groupId: number,
  isAdmin: boolean,
  permissions: GroupsPermissions,
  originalPermissions: GroupsPermissions,
  defaultGroup: Group,
  database: PermissionsDatabase,
): PermissionSectionConfig => {
  const accessPermissionConfirmations = (newValue: DataPermissionValue) => [
    getPermissionWarningModal(
      newValue,
      defaultGroupAccessPermissionValue,
      "schemas",
      defaultGroup,
      groupId,
    ),
  ];

  const accessPermissionValue = getSchemasPermission(
    permissions,
    groupId,
    entityId,
    DataPermission.VIEW_DATA,
  );
  const originalAccessPermissionValue = getSchemasPermission(
    originalPermissions,
    groupId,
    entityId,
    DataPermission.VIEW_DATA,
  );
  const defaultGroupAccessPermissionValue = getSchemasPermission(
    permissions,
    defaultGroup.id,
    entityId,
    DataPermission.VIEW_DATA,
  );
  const accessPermissionWarning = getPermissionWarning(
    accessPermissionValue,
    defaultGroupAccessPermissionValue,
    "schemas",
    defaultGroup,
    groupId,
  );

  const baseOptions = [
    DATA_PERMISSION_OPTIONS.unrestricted,
    DATA_PERMISSION_OPTIONS.controlled,
  ];
  const options = PLUGIN_ADVANCED_PERMISSIONS.addDatabasePermissionOptions(
    _.compact([
      ...baseOptions,
      originalAccessPermissionValue ===
        DATA_PERMISSION_OPTIONS.noSelfServiceDeprecated.value &&
        DATA_PERMISSION_OPTIONS.noSelfServiceDeprecated,
    ]),
    database,
  );

  // remove granular in the cases we can't provide configurability for schemas/tables
  const shouldRemoveGranularOption = _.isEqual(options, baseOptions);
  if (shouldRemoveGranularOption) {
    options.pop();
  }

  return {
    permission: DataPermission.VIEW_DATA,
    type: DataPermissionType.ACCESS,
    isDisabled: isAdmin,
    disabledTooltip: isAdmin
      ? Messages.UNABLE_TO_CHANGE_ADMIN_PERMISSIONS
      : null,
    isHighlighted: isAdmin,
    value: accessPermissionValue,
    warning: accessPermissionWarning,
    confirmations: accessPermissionConfirmations,
    options,
    postActions: {
      controlled: () =>
        limitDatabasePermission(groupId, entityId, accessPermissionValue),
      ...PLUGIN_ADMIN_PERMISSIONS_DATABASE_POST_ACTIONS,
    },
    actions: PLUGIN_ADMIN_PERMISSIONS_DATABASE_ACTIONS,
  };
};

const buildNativePermission = (
  entityId: DatabaseEntityId,
  groupId: number,
  isAdmin: boolean,
  permissions: GroupsPermissions,
  defaultGroup: Group,
  database: PermissionsDatabase,
  accessPermissionValue: DataPermissionValue,
): PermissionSectionConfig => {
  const value = getSchemasPermission(
    permissions,
    groupId,
    entityId,
    DataPermission.CREATE_QUERIES,
  );

  const defaultGroupNativePermissionValue = getSchemasPermission(
    permissions,
    defaultGroup.id,
    entityId,
    DataPermission.CREATE_QUERIES,
  );

  const disabledTooltip = getNativePermissionDisabledTooltip(
    isAdmin,
    accessPermissionValue,
  );

  const nativePermissionWarning = disabledTooltip
    ? ""
    : getPermissionWarning(
        value,
        defaultGroupNativePermissionValue,
        null,
        defaultGroup,
        groupId,
      );

  const nativePermissionConfirmations = (newValue: DataPermissionValue) => [
    getPermissionWarningModal(
      newValue,
      defaultGroupNativePermissionValue,
      null,
      defaultGroup,
      groupId,
    ),
    getViewDataPermissionsTooRestrictiveWarningModal(
      permissions,
      groupId,
      entityId,
      database,
      newValue,
    ),
  ];

  return {
    permission: DataPermission.CREATE_QUERIES,
    type: DataPermissionType.NATIVE,
    isDisabled: disabledTooltip != null,
    disabledTooltip,
    isHighlighted: isAdmin,
    value,
    warning: nativePermissionWarning,
    confirmations: nativePermissionConfirmations,
    options: [
      DATA_PERMISSION_OPTIONS.queryBuilderAndNative,
      DATA_PERMISSION_OPTIONS.queryBuilder,
      DATA_PERMISSION_OPTIONS.controlled,
      DATA_PERMISSION_OPTIONS.no,
    ],
    postActions: {
      controlled: () => navigateToGranularPermissions(groupId, entityId),
    },
  };
};

export const buildSchemasPermissions = ({
  entityId,
  groupId,
  groupType,
  permissions,
  originalPermissions,
  defaultGroup,
  database,
  permissionView,
  showTransformPermissions,
}: {
  entityId: DatabaseEntityId;
  groupId: number;
  groupType: SpecialGroupType;
  permissions: GroupsPermissions;
  originalPermissions: GroupsPermissions;
  defaultGroup: Group;
  database: PermissionsDatabase;
  permissionView: "group" | "database";
  showTransformPermissions: boolean;
}): PermissionSectionConfig[] => {
  const isAdmin = groupType === "admin";

  const accessPermission = buildAccessPermission(
    entityId,
    groupId,
    isAdmin,
    permissions,
    originalPermissions,
    defaultGroup,
    database,
  );

  const nativePermission = buildNativePermission(
    entityId,
    groupId,
    isAdmin,
    permissions,
    defaultGroup,
    database,
    accessPermission.value,
  );

  const hasAnyAccessOptions = accessPermission.options.length > 1;
  const shouldShowViewDataColumn =
    PLUGIN_ADVANCED_PERMISSIONS.shouldShowViewDataColumn && hasAnyAccessOptions;

  return _.compact([
    shouldShowViewDataColumn && accessPermission,
    nativePermission,
    ...PLUGIN_FEATURE_LEVEL_PERMISSIONS.getFeatureLevelDataPermissions({
      entityId,
      groupId,
      groupType,
      permissions,
      dataAccessPermissionValue: accessPermission.value,
      defaultGroup,
      permissionSubject: "schemas",
      permissionView,
      showTransformPermissions,
    }),
  ]);
};
